import { useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Get session ID from cookie
const getSessionId = () => {
  const nameEQ = "session_id=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

// Send analytics event via sendBeacon
const sendAnalytics = (eventType, sectionName, dwellMs = null) => {
  const sessionId = getSessionId();
  
  const data = {
    event_type: eventType,
    section_name: sectionName,
    session_id: sessionId,
    dwell_ms: dwellMs,
    page: window.location.pathname,
    referrer: document.referrer || null
  };
  
  const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
  navigator.sendBeacon(`${API}/analytics/track`, blob);
};

const useSectionTracking = () => {
  const sectionsRef = useRef(new Map()); // Store {sectionName: {entryTime, impressionSent}}
  const observerRef = useRef(null);

  useEffect(() => {
    // Find all sections with data-track-section attribute
    const sections = document.querySelectorAll('[data-track-section]');
    
    if (sections.length === 0) return;

    // Create IntersectionObserver with 50% threshold
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionName = entry.target.getAttribute('data-track-section');
          if (!sectionName) return;

          // Section is at least 50% visible
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // Check if we've already sent impression for this section
            if (!sectionsRef.current.has(sectionName)) {
              // Send section_impression event
              sendAnalytics('section_impression', sectionName);
              
              // Record entry time
              sectionsRef.current.set(sectionName, {
                entryTime: Date.now(),
                impressionSent: true
              });
            } else {
              // Re-entering section - update entry time
              const sectionData = sectionsRef.current.get(sectionName);
              sectionData.entryTime = Date.now();
            }
          } 
          // Section left view
          else if (!entry.isIntersecting) {
            const sectionData = sectionsRef.current.get(sectionName);
            if (sectionData && sectionData.entryTime) {
              // Calculate dwell time
              const dwellMs = Date.now() - sectionData.entryTime;
              
              // Send section_dwell event
              sendAnalytics('section_dwell', sectionName, dwellMs);
              
              // Clear entry time but keep impression flag
              sectionData.entryTime = null;
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.5, 1.0] // Trigger at 0%, 50%, and 100% visibility
      }
    );

    // Observe all tracked sections
    sections.forEach(section => {
      observerRef.current.observe(section);
    });

    // Flush active sections on visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        sectionsRef.current.forEach((sectionData, sectionName) => {
          if (sectionData.entryTime) {
            const dwellMs = Date.now() - sectionData.entryTime;
            sendAnalytics('section_dwell', sectionName, dwellMs);
            sectionData.entryTime = null;
          }
        });
      }
    };

    // Flush active sections on page unload
    const handleBeforeUnload = () => {
      sectionsRef.current.forEach((sectionData, sectionName) => {
        if (sectionData.entryTime) {
          const dwellMs = Date.now() - sectionData.entryTime;
          sendAnalytics('section_dwell', sectionName, dwellMs);
        }
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      if (observerRef.current) {
        sections.forEach(section => {
          observerRef.current.unobserve(section);
        });
        observerRef.current.disconnect();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return null;
};

export default useSectionTracking;
